import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, ApiError } from './admin-api.ts';
import { AdminShell, Spinner } from './ui.tsx';
import { ErrorBoundary } from './ErrorBoundary.tsx';
import { AdminLogin } from './AdminLogin.tsx';
import { EngagementsList } from './EngagementsList.tsx';
import { EngagementDetail } from './EngagementDetail.tsx';
import { EngagementForm } from './EngagementForm.tsx';
import type { EngagementListItem } from './types.ts';

// Root of the admin console. No router — a small view state switches screens.
// Relies on the QueryClientProvider already mounted in main.tsx.
export function AdminApp() {
  const [view, setView] = useState<{ name: 'list' } | { name: 'create' } | { name: 'detail'; engagement: EngagementListItem }>({
    name: 'list',
  });
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => adminApi.me(),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
  });

  const logout = async () => {
    await adminApi.logout().catch(() => {});
    setView({ name: 'list' });
    await meQuery.refetch();
  };

  if (meQuery.isLoading) {
    return (
      <AdminShell>
        <Spinner />
      </AdminShell>
    );
  }

  if (meQuery.isError || !meQuery.data) {
    return <AdminLogin onSuccess={() => void meQuery.refetch()} />;
  }

  const admin = meQuery.data;

  let screen;
  if (view.name === 'create') {
    screen = (
      <EngagementForm
        admin={admin}
        onCancel={() => setView({ name: 'list' })}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['admin-engagements'] });
          setView({ name: 'list' });
        }}
        onLogout={() => void logout()}
      />
    );
  } else if (view.name === 'detail') {
    screen = (
      <EngagementDetail
        engagement={view.engagement}
        admin={admin}
        onBack={() => setView({ name: 'list' })}
        onLogout={() => void logout()}
      />
    );
  } else {
    screen = (
      <EngagementsList
        admin={admin}
        onOpen={(engagement) => setView({ name: 'detail', engagement })}
        onCreate={() => setView({ name: 'create' })}
        onLogout={() => void logout()}
      />
    );
  }

  return <ErrorBoundary>{screen}</ErrorBoundary>;
}

export default AdminApp;
